import type { AbstractMesh, Scene } from '@babylonjs/core';
import {
  AxesViewer,
  Matrix,
  Mesh,
  MeshBuilder,
  Quaternion,
  Space,
  TransformNode,
  Vector3,
  Color3,
  ActionManager,
  ExecuteCodeAction,
  StandardMaterial,
} from '@babylonjs/core';
import type { RobotMapsService } from '@robot/shared/robot-maps.service';
import type { PrimitiveMetaData } from '../primitives/primitives';
import { parentDefault } from '@shared-modules/properties-editor-panel/pipes/editor-panel/editor-panel.pipe';
import { Voxels, VoxelsTiler } from './voxels';
import { Voxblox, type VoxbloxData } from './voxblox';
import { initMapMaterials, MapMaterial } from '../primitives/materialPrimitives';
import { DirectedGraph, MultiDirectedGraph } from 'graphology';
import { bidirectional } from 'graphology-shortest-path/unweighted';
import type { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import type { Observer } from '@babylonjs/core';
import { PointerEventTypes } from '@babylonjs/core';
import { edgePathFromNodePath } from 'graphology-shortest-path';
import { PickerOverlayService } from '../../picker-overlay.service';

export const robotGraphMetaData: PrimitiveMetaData = {
  key: 'robotMapLive',
  label: 'Robot Map Live',
  icon: 'ri-3d-box',
  editorPanels: {
    parent: parentDefault,
    properties: {
      name: true,
      position: false,
      rotation: false,
      scaling: false,
      material: false,
    },
    bindPosition: false,
    bindRotationEuler: false,
    bindRotationQuaternion: false,
    bindAdvanced: true,
    robotMap: false,
  },
};

export interface GraphEvent {
  type: string;
  details?: {
    id?: string;
  };
}

export interface GraphNode {
  id: string;
  type: string;
  edges?: Record<string, unknown>;
  data: Record<string, unknown>;
  externalData?: Record<string, unknown>;
}

export interface GraphEdge {
  id: string;
  from: string;
  to: string;
  type: string;
  data: Record<string, unknown>;
  externalData?: Record<string, unknown>;
}

export interface NodeWrapper {
  node: GraphNode;
}

export interface EdgeWrapper {
  edge: GraphEdge;
}

export interface GraphData {
  nodes: Record<string, GraphNode>;
  edges: Record<string, GraphEdge>;
}

const isNodeWrapper = (r: unknown): r is NodeWrapper => (r as NodeWrapper).node !== undefined;
const isEdgeWrapper = (r: unknown): r is EdgeWrapper => (r as EdgeWrapper).edge !== undefined;

// Node type constants
const NODE_TYPES = {
  FRAME: 'FRAME',
  PANORAMA: 'PANORAMA',
  SPOT_WAYPOINT: 'bd.spot.waypoint.v1',
  SPOT_OBJECT: 'bd.spot.object.v1',
  SPOT_POSE_SEED_LIVE: 'bd.spot.pose.seed.live.v1',
  POLYGON_NODE: 'polygon.node.v1',
  PATH_NODE: 'path.node.v1',
  VOXELS_TILER: 'voxels.tiler.v1',
  VOXELS_EXTERNAL: 'voxels.external.v1',
  VOXELS: 'voxels.v1',
  VOXBLOX: 'voxblox.v1',
  VOXBLOX_BLOCK: 'voxblox.block.v1',
} as const;

// Edge type constants
const EDGE_TYPES = {
  TRANSFORM: 'TRANSFORM',
  SPOT_TRAVERSABLE: 'bd.spot.traversable.v1',
  SPOT_OBSERVATION: 'bd.spot.observation.v1',
  POLYGON_EDGE: 'polygon.edge.v1',
  POLYGON_START: 'polygon.start.v1',
  POLYGON_BELONGS: 'polygon.belongs.v1',
  PATH_EDGE: 'path.edge.v1',
  PATH_START: 'path.start.v1',
  CONTAINS_TILE: 'contains.tile',
  ROS_PC2_OBSERVATION: 'ros.pc2.observation.v1',
} as const;

const VOXEL_DATA_KEY = 'voxelData';

export class RobotGraph extends Mesh {
  private meshes: Map<string, Mesh | TransformNode> = new Map<string, Mesh | TransformNode>();
  private nodes: Record<string, GraphNode> = {};
  private edges: Record<string, GraphEdge> = {};
  private transformTree: MultiDirectedGraph = new MultiDirectedGraph();
  private nodeRenderers: Record<string, (nodeId: string) => Mesh | TransformNode | null> = {};
  private edgeRenderers: Record<string, (edgeId: string) => Mesh | null> = {};
  private mapSubscription: Subscription;

  private clickToRenderActive = false;
  private activeCanvas: HTMLCanvasElement | null = null;
  private originalCursor: string = '';
  private activeEscapeHandler: ((evt: KeyboardEvent) => void) | null = null;
  private activeTilerClickHandlers: Map<string, { dispose: () => void }> | null = null;
  private activeOnComplete: (() => void) | null = null;
  private activeVoxelOverlay: HTMLElement | null = null;
  private voxelOverlayCleanupFunction: (() => void) | null = null;
  private nodeTypeOverlayCleanupFunction: (() => void) | null = null;

  private isLockToNodeTypeMode: boolean = false;
  private nodeTypeClickHandlers: Map<string, { dispose: () => void }> = new Map();
  private nodeTypeHoverIndicators: Map<string, AbstractMesh> = new Map();
  private nodeIDCurrentlyHovered: string | null = null;
  private lockToNodeTypePointerObserver: Observer<any> | null = null;
  private lockToNodeTypeOnNodeSelected: ((nodeId: string) => void) | null = null;
  private lockToNodeTypeCanvas: HTMLCanvasElement | null = null;
  private lockToNodeTypeOriginalCursor: string | null = null;

  constructor(
    name: string,
    private scene: Scene,
    private robotMapService: RobotMapsService,
    private projectId: string,
    private sourceKey: string,
    private pickerOverlayService: PickerOverlayService,
    private debug: boolean = false,
  ) {
    super(name, scene);
    initMapMaterials(scene);
    this.initNodeRenderers();
    this.initEdgeRenderers();

    if (this.debug) addAxes(this);
  }

  public override setEnabled(value: boolean) {
    super.setEnabled(value);
    this.meshes.forEach((mesh) => mesh.setEnabled(value));
  }

  public override dispose(doNotRecurse?: boolean, disposeMaterialAndTextures?: boolean): void {
    this.clear();
    this.mapSubscription?.unsubscribe();
    super.dispose(doNotRecurse, disposeMaterialAndTextures);
  }

  public get nodeCount(): number {
    return this.transformTree.order;
  }

  public get edgeCount(): number {
    return this.transformTree.size;
  }

  public getMesh(meshName: string): AbstractMesh | TransformNode | null {
    const node = this.meshes.get(meshName);
    if (node) return node as AbstractMesh | TransformNode;
    return null;
  }

  public enableClickToRenderMode(onComplete: () => void): void {
    if (this.clickToRenderActive) {
      return;
    }

    const canvas = this.scene.getEngine().getRenderingCanvas();

    this.clickToRenderActive = true;
    this.activeCanvas = canvas;
    this.originalCursor = canvas.style.cursor;
    this.activeOnComplete = onComplete;

    canvas.style.cursor = 'pointer';

    this.voxelOverlayCleanupFunction = this.pickerOverlayService.show(
      {
        title: 'Render External Voxels',
        instructions: ['Click on a tiler node to render its external voxels'],
        showEscapeHint: true,
      },
      () => {
        this.cleanupClickToRenderMode(
          canvas,
          this.originalCursor,
          this.activeEscapeHandler,
          this.activeTilerClickHandlers,
        );
        this.resetClickToRenderState();
        onComplete();
      },
    );

    this.activeTilerClickHandlers = this.setupTilerNodeClickHandlers(
      canvas,
      this.originalCursor,
      null, // Escape handler will be set up below
      onComplete,
    );

    this.activeEscapeHandler = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        this.cleanupClickToRenderMode(
          canvas,
          this.originalCursor,
          this.activeEscapeHandler,
          this.activeTilerClickHandlers,
        );
        this.resetClickToRenderState();
        onComplete();
      }
    };

    document.addEventListener('keydown', this.activeEscapeHandler);
  }

  public cancelClickToRenderMode(): void {
    if (!this.clickToRenderActive || !this.activeCanvas) {
      return;
    }

    this.cleanupClickToRenderMode(
      this.activeCanvas,
      this.originalCursor,
      this.activeEscapeHandler,
      this.activeTilerClickHandlers,
    );

    this.resetClickToRenderState();

    if (this.activeOnComplete) {
      this.activeOnComplete();
    }
  }

  public enableLockToNodeTypeMode(
    nodeType: string,
    onNodeSelected: (nodeId: string) => void,
    onCancelled?: () => void,
  ): void {
    if (this.isLockToNodeTypeMode) {
      console.warn('Lock to Node Type mode already active');
      return;
    }

    const canvas = this.scene.getEngine().getRenderingCanvas();
    if (!canvas) {
      console.error('Canvas not found');
      return;
    }

    const matchingNodes = this.findNodesByType(nodeType);
    if (matchingNodes.length === 0) {
      console.warn(`No nodes found matching type: ${nodeType}`, Object.keys(this.nodes));
      this.cleanupLockToNodeTypeMode(canvas, canvas.style.cursor);
      if (onCancelled) onCancelled();
      return;
    }

    this.isLockToNodeTypeMode = true;
    const originalCursor = canvas.style.cursor;
    canvas.style.cursor = 'pointer';

    this.lockToNodeTypeOnNodeSelected = onNodeSelected;
    this.lockToNodeTypeCanvas = canvas;
    this.lockToNodeTypeOriginalCursor = originalCursor;

    this.setupLockToNodeTypeClickDetection();

    this.nodeTypeOverlayCleanupFunction = this.pickerOverlayService.show(
      {
        title: `Select ${nodeType} node`,
        instructions: ['Click on a node to select it'],
        showEscapeHint: true,
      },
      () => {
        this.cleanupLockToNodeTypeMode(canvas, originalCursor);
        if (onCancelled) onCancelled();
      },
    );

    matchingNodes.forEach((nodeInfo) => {
      const handler = this.createNodeTypeHoverAndClickHandler(nodeInfo, onNodeSelected, canvas, originalCursor);
      this.nodeTypeClickHandlers.set(nodeInfo.nodeId, handler);
    });
  }

  public cancelLockToNodeTypeMode(): void {
    if (!this.isLockToNodeTypeMode) {
      return;
    }

    const canvas = this.scene.getEngine().getRenderingCanvas();
    if (!canvas) {
      return;
    }

    this.cleanupLockToNodeTypeMode(canvas, 'default');
  }

  public async renderBoundUpdate(update: GraphEvent[]): Promise<void> {
    if (!this.isEnabled()) return;

    const updateNodes: Record<string, GraphNode> = {};
    const updateEdges: Record<string, GraphEdge> = {};

    update.forEach((event) => {
      switch (event.type) {
        case 'maps.MapClearedEvent':
          this.clear();
          break;
        case 'maps.NodeDataChangedEvent':
        case 'maps.NodeAddedEvent':
          updateNodes[event.details.id] = event.details as GraphNode;
          break;
        case 'maps.EdgeDataChangedEvent':
        case 'maps.EdgeAddedEvent':
          updateEdges[event.details.id] = event.details as GraphEdge;
          break;
        case 'maps.NodeDeletedEvent':
          this.deleteNode(event.details.id);
          break;
        case 'maps.EdgeDeletedEvent':
          this.deleteEdge(event.details.id);
          break;
      }
    });

    this.renderGraph({ nodes: updateNodes, edges: updateEdges });
  }

  public getGraphFromServiceCall(): void {
    const nodes: Record<string, GraphNode> = {};
    const edges: Record<string, GraphEdge> = {};

    try {
      const { callsign, source } = this.getServiceSourceFromBoundUpdate(this.sourceKey);
      if (!callsign || !source) return;

      this.mapSubscription?.unsubscribe();
      this.mapSubscription = this.robotMapService
        .callServiceStream(this.projectId, callsign, source, { mode: 'progressive' })
        .pipe(
          finalize(() => {
            this.renderGraph({ nodes, edges });
          }),
        )
        .subscribe((response) => {
          if (!response) return;
          if (isNodeWrapper(response)) {
            nodes[response.node.id] = response.node;
          }
          if (isEdgeWrapper(response)) {
            edges[response.edge.id] = response.edge;
          }
        });
    } catch (error) {
      console.error('Error loading dynamic map', error);
    }
  }

  private setupTilerNodeClickHandlers(
    canvas: HTMLCanvasElement,
    originalCursor: string,
    escapeHandler: ((evt: KeyboardEvent) => void) | null,
    onComplete: () => void,
  ): Map<string, { dispose: () => void }> {
    const tilerClickHandlers = new Map<string, { dispose: () => void }>();
    const hoverIndicators = new Map<string, AbstractMesh>();

    for (const [nodeId, node] of Object.entries(this.nodes)) {
      if (node.type !== NODE_TYPES.VOXELS_TILER) continue;

      const tilerMesh = this.meshes.get(nodeId);
      if (!tilerMesh) {
        console.warn(`Tiler mesh not found for node ${nodeId}`);
        continue;
      }

      // Create an invisible clickable area for the tiler node (since VoxelsTiler has no visible geometry)
      const clickableArea = MeshBuilder.CreateSphere(`${nodeId}_clickArea`, { diameter: 1.0, segments: 8 }, this.scene);

      const tilerPosition = tilerMesh.getAbsolutePosition();
      clickableArea.position = tilerPosition.clone();

      clickableArea.visibility = 0;

      const actionManager = new ActionManager(this.scene);
      clickableArea.actionManager = actionManager;

      const hoverEnterAction = new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, () => {
        if (!hoverIndicators.has(nodeId)) {
          const hoverSphere = this.createTilerHoverIndicator(nodeId, tilerPosition);
          hoverIndicators.set(nodeId, hoverSphere);
        }
      });

      const hoverLeaveAction = new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, () => {
        const hoverIndicator = hoverIndicators.get(nodeId);
        if (hoverIndicator) {
          hoverIndicator.dispose();
          hoverIndicators.delete(nodeId);
        }
      });

      const clickAction = new ExecuteCodeAction(ActionManager.OnPickTrigger, () => {
        console.log(`Tiler node ${nodeId} clicked, starting render...`);

        this.cleanupClickToRenderMode(canvas, originalCursor, escapeHandler, tilerClickHandlers);
        this.resetClickToRenderState();

        // Handle async rendering without making the action handler async
        this.renderExternalVoxelsForTiler(nodeId)
          .then(() => {
            console.log(`Successfully rendered external voxels for tiler ${nodeId}`);
          })
          .catch((error) => {
            console.error('Error rendering external voxels for tiler:', error);
          })
          .finally(() => {
            onComplete();
          });
      });

      actionManager.registerAction(hoverEnterAction);
      actionManager.registerAction(hoverLeaveAction);
      actionManager.registerAction(clickAction);

      tilerClickHandlers.set(nodeId, {
        dispose: () => {
          const hoverIndicator = hoverIndicators.get(nodeId);
          if (hoverIndicator) {
            hoverIndicator.dispose();
            hoverIndicators.delete(nodeId);
          }
          clickableArea.dispose();
        },
      });
    }

    return tilerClickHandlers;
  }

  private resetClickToRenderState(): void {
    this.clickToRenderActive = false;
    this.activeCanvas = null;
    this.originalCursor = '';
    this.activeEscapeHandler = null;
    this.activeTilerClickHandlers = null;
    this.activeOnComplete = null;
    this.activeVoxelOverlay = null;
    this.voxelOverlayCleanupFunction = null;
    this.nodeTypeOverlayCleanupFunction = null;
  }

  private createTilerHoverIndicator(nodeId: string, position: Vector3): AbstractMesh {
    // Create a small teal sphere to indicate hover
    const hoverSphere = MeshBuilder.CreateSphere(
      `${nodeId}_hoverIndicator`,
      {
        diameter: 0.3,
        segments: 8,
      },
      this.scene,
    );

    hoverSphere.position = position.clone();

    const hoverMaterial = new StandardMaterial(`${nodeId}_hoverMaterial`, this.scene);
    hoverMaterial.diffuseColor = Color3.Teal();
    hoverMaterial.specularColor = new Color3(0, 0, 0); // No specular reflection
    hoverMaterial.alpha = 0.8; // Semi-transparent
    hoverSphere.material = hoverMaterial;

    return hoverSphere;
  }

  private cleanupClickToRenderMode(
    canvas: HTMLCanvasElement,
    originalCursor: string,
    escapeHandler?: ((evt: KeyboardEvent) => void) | null,
    tilerClickHandlers?: Map<string, { dispose: () => void }> | null,
  ): void {
    canvas.style.cursor = originalCursor;

    if (this.voxelOverlayCleanupFunction) {
      this.voxelOverlayCleanupFunction();
      this.voxelOverlayCleanupFunction = null;
    }

    if (tilerClickHandlers) {
      tilerClickHandlers.forEach((handler) => handler.dispose());
      tilerClickHandlers.clear();
    }

    if (escapeHandler) {
      document.removeEventListener('keydown', escapeHandler);
    }
  }

  private initNodeRenderers(): void {
    this.nodeRenderers = {
      [NODE_TYPES.FRAME]: this.renderTransformNode.bind(this),
      [NODE_TYPES.PANORAMA]: (nodeId) => this.renderCylinder(nodeId, this.getMaterial(MapMaterial.WHITE), 1, 0.5),
      [NODE_TYPES.SPOT_WAYPOINT]: (nodeId) => this.renderSphere(nodeId, this.getMaterial(MapMaterial.YELLOW), 0.2),
      [NODE_TYPES.SPOT_OBJECT]: (nodeId) => this.renderSphere(nodeId, this.getMaterial(MapMaterial.TEAL), 0.1),
      [NODE_TYPES.SPOT_POSE_SEED_LIVE]: (nodeId) =>
        this.renderSphere(nodeId, this.getMaterial(MapMaterial.YELLOW), 0.1),
      [NODE_TYPES.POLYGON_NODE]: (nodeId) => this.renderSphere(nodeId, this.getMaterial(MapMaterial.RED), 0.1),
      [NODE_TYPES.PATH_NODE]: (nodeId) => this.renderSphere(nodeId, this.getMaterial(MapMaterial.GREEN), 0.1),
      [NODE_TYPES.VOXELS_TILER]: this.renderVoxelsTiler.bind(this),
      // NOTE: External voxels are rendered on demand through click-to-render.
      // So we intentionally set this renderer to null.
      [NODE_TYPES.VOXELS_EXTERNAL]: () => null,
      [NODE_TYPES.VOXELS]: this.renderVoxels.bind(this),
      [NODE_TYPES.VOXBLOX]: this.renderTransformNode.bind(this),
      [NODE_TYPES.VOXBLOX_BLOCK]: this.renderVoxblox.bind(this),
      undefined: () => null,
    };
  }

  private initEdgeRenderers(): void {
    this.edgeRenderers = {
      [EDGE_TYPES.TRANSFORM]: this.renderTransform.bind(this),
      [EDGE_TYPES.SPOT_TRAVERSABLE]: (edgeId) => this.renderTube(edgeId, this.getMaterial(MapMaterial.YELLOW), 0.05),
      [EDGE_TYPES.SPOT_OBSERVATION]: (edgeId) => this.renderTube(edgeId, this.getMaterial(MapMaterial.TEAL), 0.025),
      [EDGE_TYPES.POLYGON_EDGE]: (edgeId) => this.renderTube(edgeId, this.getMaterial(MapMaterial.RED), 0.025),
      [EDGE_TYPES.POLYGON_START]: null,
      [EDGE_TYPES.POLYGON_BELONGS]: null,
      [EDGE_TYPES.PATH_EDGE]: (edgeId) => this.renderTube(edgeId, this.getMaterial(MapMaterial.GREEN), 0.05),
      [EDGE_TYPES.PATH_START]: (edgeId) => this.renderTube(edgeId, this.getMaterial(MapMaterial.GREEN), 0.05),
      [EDGE_TYPES.CONTAINS_TILE]: () => null,
      [EDGE_TYPES.ROS_PC2_OBSERVATION]: () => null,
      undefined: () => null,
    };
  }

  private deleteNode(nodeId: string): void {
    this.deleteMesh(nodeId);
    if (!this.transformTree.hasNode(nodeId)) return;
    this.transformTree.dropNode(nodeId);
  }

  private deleteEdge(edgeId: string): void {
    this.deleteMesh(edgeId);
    if (!this.transformTree.hasEdge(edgeId)) return;
    this.transformTree.dropEdge(edgeId);
  }

  private renderGraph(graph: GraphData): void {
    if (!this.parent) return;

    const parentName = (this.parent as any)['displayName'];
    if (!parentName) return;

    this.meshes.set(parentName, this.parent as TransformNode | Mesh);
    this.transformTree.mergeNode(parentName);

    Object.entries(graph.nodes).forEach(([nodeID, node]) => {
      this.transformTree.mergeNode(nodeID, node);
    });

    Object.entries(graph.edges).forEach(([edgeID, edge]) => {
      if (edge?.type === EDGE_TYPES.TRANSFORM) {
        if (!edge.data['TransformMatrix']) {
          console.warn(`TransformMatrix missing for edge ${edgeID}`);
          return;
        }
        const matrix = Matrix.FromArray(edge.data['TransformMatrix'] as unknown as number[]).transpose();
        this.transformTree.mergeDirectedEdgeWithKey(edgeID, edge.from, edge.to, { matrix });
      }
    });

    this.nodes = { ...this.nodes, ...graph.nodes };
    this.edges = { ...this.edges, ...graph.edges };

    const nodesToRender = new Set(Object.keys(graph.nodes));
    const edgesToRender = new Set(Object.keys(graph.edges));

    // We do shortest path search from the parent to each node to render,
    // so we can handle messages that come in out of order.
    for (const node of Object.values(graph.nodes)) {
      const nodePath = bidirectional(this.transformTree, parentName, node.id);
      if (!nodePath) continue;

      const edgePath = edgePathFromNodePath(this.transformTree, nodePath);
      for (let i = 0; i < edgePath.length; i++) {
        const edgeID = edgePath[i];
        const fromNodeID = nodePath[i];
        const toNodeID = nodePath[i + 1];

        if (!nodesToRender.has(toNodeID)) continue;
        nodesToRender.delete(toNodeID);

        const fromNode = this.meshes.get(fromNodeID);
        if (!fromNode) continue;

        const matrix = this.transformTree.getEdgeAttribute(edgeID, 'matrix');
        if (!matrix) continue;

        this.renderNode(fromNode, matrix, toNodeID);

        Object.keys(node.edges).forEach((edgeId) => {
          edgesToRender.add(edgeId);
        });
      }
    }

    edgesToRender.forEach((edgeID) => {
      edgesToRender.delete(edgeID);
      this.renderEdge(edgeID);

      if (this.edges[edgeID]?.type === EDGE_TYPES.TRANSFORM) {
        this.updateTransformMatrix(this.edges[edgeID]);
      }
    });
  }

  private updateTransformMatrix(edge: GraphEdge): void {
    if (!this.transformTree.hasEdge(edge.id)) return;

    const matrix = this.transformTree.getEdgeAttribute(edge.id, 'matrix');
    if (!matrix) return;

    const fromNode = this.meshes.get(edge.from);
    const toNode = this.meshes.get(edge.to);
    if (!fromNode || !toNode) return;

    if (fromNode !== toNode.parent) return;

    applyMatrix(toNode, matrix);
  }

  private getServiceSourceFromBoundUpdate(sourceKey: string): { callsign: string | null; source: string | null } {
    if (!sourceKey) return { callsign: null, source: null };

    const callsign = sourceKey.trim().split('/')[1];
    const source = '/' + sourceKey.trim().split('?')[0].split('/').slice(2).join('/') + '/get';
    return { callsign, source };
  }

  private getExternalDataServiceSourceFromBoundUpdate(sourceKey: string): {
    callsign: string | null;
    source: string | null;
  } {
    if (!sourceKey) return { callsign: null, source: null };

    const callsign = sourceKey.trim().split('/')[1];
    const source = '/' + sourceKey.trim().split('?')[0].split('/').slice(2).join('/') + '/nodes/get/externalData/byKey';
    return { callsign, source };
  }

  private clear(): void {
    const oldGraph = this.transformTree;
    const oldMeshes = new Map(this.meshes);
    this.transformTree = new DirectedGraph();
    this.meshes = new Map<string, Mesh | TransformNode>();

    oldGraph.clear();
    oldMeshes.forEach((mesh) => {
      if (mesh !== this.parent) mesh.dispose();
    });
  }

  private deleteMesh(meshName: string): void {
    const mesh = this.meshes.get(meshName);

    if (mesh === this.parent || mesh === this) return;

    if (mesh) mesh.dispose();
    this.meshes.delete(meshName);
  }

  private getMaterial(materialName: string): StandardMaterial {
    return this.scene.getMaterialByName(materialName) as StandardMaterial;
  }

  private renderNode(parentNode: TransformNode | Mesh, matrix: Matrix, nodeId: string): void {
    const nodeData = this.nodes[nodeId];
    if (!nodeData) return;

    let nodeMesh: Mesh | TransformNode;
    const renderFunction = this.nodeRenderers[nodeData.type];
    if (renderFunction) {
      nodeMesh = renderFunction(nodeId);
    }

    if (!nodeMesh) return;

    applyMatrix(nodeMesh, matrix);
    nodeMesh.parent = parentNode;

    this.meshes.set(nodeId, nodeMesh);
    nodeMesh.setEnabled(this.isEnabled());
  }

  private renderTransformNode(nodeId: string): TransformNode {
    let transformNode = this.getMesh(nodeId) as TransformNode;
    if (transformNode) return transformNode;

    transformNode = new TransformNode(nodeId, this.scene);

    if (this.debug) addAxes(transformNode);

    return transformNode;
  }

  private renderCylinder(nodeId: string, material: StandardMaterial, diameter: number, height: number): Mesh {
    if (this.getMesh(nodeId)) return this.getMesh(nodeId) as Mesh;

    const cylinder = MeshBuilder.CreateCylinder(nodeId, { diameter, height }, this.scene);
    cylinder.rotate(new Vector3(1, 0, 0), Math.PI / 2, Space.LOCAL);
    cylinder.material = material;
    return cylinder;
  }

  private renderSphere(nodeId: string, material: StandardMaterial, diameter: number): Mesh {
    if (this.getMesh(nodeId)) return this.getMesh(nodeId) as Mesh;

    const sphere = MeshBuilder.CreateSphere(nodeId, { diameter, segments: 8 }, this.scene);
    sphere.material = material;
    return sphere;
  }

  private renderVoxelsTiler(nodeId: string): VoxelsTiler {
    return (this.getMesh(nodeId) as VoxelsTiler) || new VoxelsTiler(nodeId, this.scene);
  }

  private renderVoxels(nodeId: string): AbstractMesh | TransformNode {
    const voxelsMesh = this.getMesh(nodeId) || new Voxels(nodeId, this.scene);
    const nodeData = this.nodes[nodeId].data;
    const voxelData = nodeData[VOXEL_DATA_KEY] as unknown as string;
    if (!voxelData || !(voxelsMesh instanceof Voxels)) {
      return voxelsMesh;
    }
    voxelsMesh.renderBoundUpdate(voxelData);
    return voxelsMesh;
  }

  private async fetchExternalVoxelData(nodeId: string): Promise<string | null> {
    const { callsign, source } = this.getExternalDataServiceSourceFromBoundUpdate(this.sourceKey);
    if (!callsign) {
      console.warn(`No callsign found for source key ${this.sourceKey}`);
      return null;
    }
    if (!source) {
      console.warn(`No source found for source key ${this.sourceKey}`);
      return null;
    }

    try {
      const payload = {
        id: nodeId,
        key: VOXEL_DATA_KEY,
      };

      const response = await this.robotMapService.callServiceRaw(this.projectId, callsign, source, payload);

      if (!response) {
        console.warn(`No response received for node ${nodeId}`);
        return null;
      }

      return response as string;
    } catch (err) {
      console.error(`Error fetching external voxelData for node ${nodeId}:`, err);
      return null;
    }
  }

  private renderVoxblox(nodeId: string): Mesh {
    const voxbloxMesh = (this.getMesh(nodeId) as Voxblox) || new Voxblox(nodeId, this.scene);
    const nodeData = this.nodes[nodeId];
    if (!nodeData || !(voxbloxMesh instanceof Voxblox)) {
      return voxbloxMesh;
    }
    voxbloxMesh.renderBoundUpdate(nodeData.data as unknown as VoxbloxData);
    return voxbloxMesh;
  }

  private renderEdge(edgeId: string): void {
    const graphEdge = this.edges[edgeId];
    if (!graphEdge) return;

    let edgeMesh: Mesh;
    const renderFunction = this.edgeRenderers[graphEdge.type];
    if (renderFunction) {
      edgeMesh = renderFunction(edgeId);
    }

    if (!edgeMesh) return;

    this.meshes.set(edgeId, edgeMesh);
    edgeMesh.setEnabled(this.isEnabled());
  }

  private getNodeMeshesForEdge(
    edgeId: string,
    fromId: string,
    toId: string,
  ): { fromNode: TransformNode | Mesh | null; toNode: TransformNode | Mesh | null } | null {
    this.deleteMesh(edgeId);
    const fromNodeMesh = this.getMesh(fromId);
    const toNodeMesh = this.getMesh(toId);
    if (!fromNodeMesh || !toNodeMesh) return null;
    fromNodeMesh?.computeWorldMatrix(true);
    toNodeMesh?.computeWorldMatrix(true);
    return { fromNode: fromNodeMesh, toNode: toNodeMesh };
  }

  private renderTransform(edgeId: string): Mesh | null {
    if (!this.debug) return null;

    const edgeData = this.edges[edgeId];
    const nodeMeshes = this.getNodeMeshesForEdge(edgeId, edgeData.from, edgeData.to);
    if (!nodeMeshes) return null;

    this.getMesh(edgeId)?.dispose();
    return this.renderDashedLine(edgeId, 1.0, 0.1);
  }

  private getEdgePath(
    edgeId: string,
  ): { fromNode: TransformNode | Mesh; toNode: TransformNode | Mesh; path: Vector3[] } | null {
    const graphEdge = this.edges[edgeId];
    const nodeMeshes = this.getNodeMeshesForEdge(edgeId, graphEdge.from, graphEdge.to);
    if (!nodeMeshes) return null;

    const { fromNode, toNode } = nodeMeshes;
    const path = [fromNode.getAbsolutePosition(), toNode.getAbsolutePosition()];
    if (path[0].equals(path[1])) return null;

    return { fromNode, toNode, path };
  }

  private renderDashedLine = (edgeId: string, dashSize: number, gapSize: number): Mesh | null => {
    const edgePath = this.getEdgePath(edgeId);
    if (!edgePath) return null;

    const { fromNode, path } = edgePath;
    const dashedLines = MeshBuilder.CreateDashedLines(edgeId, { points: path, dashSize, gapSize }, this.scene);
    dashedLines.setParent(fromNode);
    return dashedLines;
  };

  private renderTube = (edgeId: string, material: StandardMaterial, radius: number): Mesh | null => {
    const edgePath = this.getEdgePath(edgeId);
    if (!edgePath) return null;

    const { fromNode, path } = edgePath;
    const tube = MeshBuilder.CreateTube(edgeId, { path, radius, tessellation: 16 }, this.scene);
    tube.material = material;
    tube.setParent(fromNode);
    return tube;
  };

  private async renderExternalVoxelsForTiler(tilerNodeId: string): Promise<void> {
    const connectedExternalNodes = this.findTilerExternalVoxelNodes(tilerNodeId);

    if (connectedExternalNodes.length === 0) {
      console.warn(`No external voxel nodes found for tiler ${tilerNodeId}`);
      return;
    }

    console.log(
      `Found voxel tiler node: ${tilerNodeId}; with ${connectedExternalNodes.length} external voxel nodes connected`,
    );

    await this.renderExternalVoxels(connectedExternalNodes);
  }

  private findTilerExternalVoxelNodes(
    tilerNodeId: string,
  ): { nodeId: string; matrix: Matrix; parent: TransformNode | Mesh }[] {
    const connectedExternalNodes: { nodeId: string; matrix: Matrix; parent: TransformNode | Mesh }[] = [];

    const tilerNode = this.nodes[tilerNodeId];
    if (!tilerNode?.edges) {
      return connectedExternalNodes;
    }

    const tilerMesh = this.meshes.get(tilerNodeId);
    if (!tilerMesh) {
      return connectedExternalNodes;
    }

    // Look through the tiler node's edges for TRANSFORM edges to external voxel nodes.
    for (const [edgeId] of Object.entries(tilerNode.edges)) {
      const edge = this.edges[edgeId];
      if (!edge || edge.type !== EDGE_TYPES.TRANSFORM || edge.from !== tilerNodeId) {
        continue;
      }

      const externalNodeId = edge.to;
      const externalNode = this.nodes[externalNodeId];

      if (!externalNode || externalNode.type !== NODE_TYPES.VOXELS_EXTERNAL) {
        continue;
      }

      const tformMatrix = this.transformTree.getEdgeAttribute(edgeId, 'matrix');
      if (!tformMatrix) {
        continue;
      }

      connectedExternalNodes.push({
        nodeId: externalNodeId,
        matrix: tformMatrix,
        parent: tilerMesh,
      });
    }

    return connectedExternalNodes;
  }

  private async renderExternalVoxels(
    externalNodes: { nodeId: string; matrix: Matrix; parent: TransformNode | Mesh }[],
  ): Promise<void> {
    for (const externalNode of externalNodes) {
      try {
        let voxelsMesh = this.getMesh(externalNode.nodeId) as Voxels;
        if (!voxelsMesh || !(voxelsMesh instanceof Voxels)) {
          voxelsMesh = new Voxels(externalNode.nodeId, this.scene);
          this.meshes.set(externalNode.nodeId, voxelsMesh);
        }

        applyMatrix(voxelsMesh, externalNode.matrix);
        voxelsMesh.parent = externalNode.parent;
        voxelsMesh.setEnabled(this.isEnabled());

        const voxelData = await this.fetchExternalVoxelData(externalNode.nodeId);
        if (!voxelData) {
          console.warn(`No voxel data received for node: ${externalNode.nodeId}`);
          continue;
        }

        voxelsMesh.renderBoundUpdate(voxelData);
      } catch (error) {
        console.error(`Error rendering external voxel node ${externalNode.nodeId}:`, error);
      }
    }
  }

  private findNodesByType(nodeType: string): { nodeId: string; position: Vector3 }[] {
    const matchingNodes: { nodeId: string; position: Vector3 }[] = [];

    for (const [nodeId, node] of Object.entries(this.nodes)) {
      if (node.type === nodeType) {
        const mesh = this.meshes.get(nodeId);
        if (mesh) {
          matchingNodes.push({
            nodeId,
            position: mesh.getAbsolutePosition(),
          });
        }
      }
    }

    return matchingNodes;
  }

  private setupLockToNodeTypeClickDetection(): void {
    if (this.lockToNodeTypePointerObserver) {
      this.scene.onPointerObservable.remove(this.lockToNodeTypePointerObserver);
      this.lockToNodeTypePointerObserver = null;
    }

    this.lockToNodeTypePointerObserver = this.scene.onPointerObservable.add(
      (pointerInfo) => {
        if (
          pointerInfo.type === PointerEventTypes.POINTERUP &&
          this.nodeIDCurrentlyHovered &&
          this.lockToNodeTypeOnNodeSelected &&
          this.lockToNodeTypeCanvas &&
          this.lockToNodeTypeOriginalCursor !== null
        ) {
          const callback = this.lockToNodeTypeOnNodeSelected;
          const selectedNodeId = this.nodeIDCurrentlyHovered;

          this.cleanupLockToNodeTypeMode(this.lockToNodeTypeCanvas, this.lockToNodeTypeOriginalCursor);
          callback(selectedNodeId);
        }
      },
      -1, // High priority to execute before other observers
    );
  }

  private createNodeTypeHoverAndClickHandler(
    nodeInfo: { nodeId: string; position: Vector3 },
    _onNodeSelected: (nodeId: string) => void,
    _canvas: HTMLCanvasElement,
    _originalCursor: string,
  ): { dispose: () => void } {
    const interactionArea = MeshBuilder.CreateSphere(
      `${nodeInfo.nodeId}_lockToNodeType_interactionArea`,
      {
        diameter: 0.8,
        segments: 8,
      },
      this.scene,
    );

    interactionArea.position = nodeInfo.position.clone();
    interactionArea.visibility = 0; // Invisible but still detectable for hover

    const actionManager = new ActionManager(this.scene);
    interactionArea.actionManager = actionManager;

    const hoverEnterAction = new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, () => {
      this.nodeIDCurrentlyHovered = nodeInfo.nodeId;
      if (!this.nodeTypeHoverIndicators.has(nodeInfo.nodeId)) {
        const hoverSphere = this.createNodeTypeHoverIndicator(nodeInfo.nodeId, nodeInfo.position);
        this.nodeTypeHoverIndicators.set(nodeInfo.nodeId, hoverSphere);
      }
    });

    const hoverLeaveAction = new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, () => {
      this.nodeIDCurrentlyHovered = null;
      const hoverIndicator = this.nodeTypeHoverIndicators.get(nodeInfo.nodeId);
      if (hoverIndicator) {
        hoverIndicator.dispose();
        this.nodeTypeHoverIndicators.delete(nodeInfo.nodeId);
      }
    });

    // Note: Click detection is handled at scene level because ActionManager.OnPickTrigger
    // gets blocked by overlays. The scene-level click detection will check nodeIDCurrentlyHovered
    // and call the onNodeSelected callback.

    actionManager.registerAction(hoverEnterAction);
    actionManager.registerAction(hoverLeaveAction);

    return {
      dispose: () => {
        if (this.nodeIDCurrentlyHovered === nodeInfo.nodeId) {
          this.nodeIDCurrentlyHovered = null;
        }
        const hoverIndicator = this.nodeTypeHoverIndicators.get(nodeInfo.nodeId);
        if (hoverIndicator) {
          hoverIndicator.dispose();
          this.nodeTypeHoverIndicators.delete(nodeInfo.nodeId);
        }
        interactionArea.dispose();
      },
    };
  }

  private createNodeTypeHoverIndicator(nodeId: string, position: Vector3): AbstractMesh {
    const hoverSphere = MeshBuilder.CreateSphere(
      `${nodeId}_lockToNodeType_hoverIndicator`,
      {
        diameter: 0.4,
        segments: 8,
      },
      this.scene,
    );

    hoverSphere.position = position.clone();

    const hoverMaterial = new StandardMaterial(`${nodeId}_lockToNodeType_hoverMaterial`, this.scene);
    hoverMaterial.diffuseColor = new Color3(0, 0.5, 1); // Blue color
    hoverMaterial.specularColor = new Color3(0, 0, 0);
    hoverMaterial.alpha = 0.8;
    hoverSphere.material = hoverMaterial;

    return hoverSphere;
  }

  private cleanupLockToNodeTypeMode(canvas: HTMLCanvasElement, originalCursor: string): void {
    this.isLockToNodeTypeMode = false;

    canvas.style.cursor = originalCursor;

    if (this.nodeTypeOverlayCleanupFunction) {
      this.nodeTypeOverlayCleanupFunction();
      this.nodeTypeOverlayCleanupFunction = null;
    }

    if (this.lockToNodeTypePointerObserver) {
      this.scene.onPointerObservable.remove(this.lockToNodeTypePointerObserver);
      this.lockToNodeTypePointerObserver = null;
    }

    this.lockToNodeTypeOnNodeSelected = null;
    this.lockToNodeTypeCanvas = null;
    this.lockToNodeTypeOriginalCursor = null;

    this.nodeIDCurrentlyHovered = null;

    this.nodeTypeClickHandlers.forEach((handler) => handler.dispose());
    this.nodeTypeClickHandlers.clear();

    this.nodeTypeHoverIndicators.forEach((indicator) => {
      if (indicator && typeof indicator.dispose === 'function') {
        indicator.dispose();
      }
    });
    this.nodeTypeHoverIndicators.clear();
  }
}

const applyMatrix = (node: TransformNode | Mesh, matrix: Matrix): void => {
  const scale = new Vector3();
  const rotation = new Quaternion();
  const translation = new Vector3();
  matrix.decompose(scale, rotation, translation);
  node.position = translation;
  node.rotationQuaternion = rotation;
  node.scaling = scale;
  node.computeWorldMatrix(true);
};

const addAxes = (parent: TransformNode | Mesh): void => {
  const axes = new AxesViewer(parent.getScene());
  axes.xAxis.parent = parent;
  axes.yAxis.parent = parent;
  axes.zAxis.parent = parent;
};
